import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Download, Loader2, RefreshCw, Wallet } from 'lucide-react';

import { downloadCsv } from '@/lib/csvExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Tutor Earnings — Admin V5
 *
 * Projected payout per tutor for a given UTC month, based on the 70% share of
 * successful Paystack payments linked to student enquiries assigned to that
 * tutor. A compact prev/next navigator lets admins scroll through historical
 * months for bookkeeping. View-only. No money movement.
 */

const currentYm = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

const shiftYm = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (isoMonth) => {
  if (!isoMonth) return '';
  const [y, m] = isoMonth.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

const TutorEarningsCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentYm());

  const today = currentYm();
  const isCurrent = selectedMonth === today;

  const load = useCallback(async (ym) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payments/admin/tutor-earnings?month=${ym}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error('tutor-earnings fetch failed', e);
      setError(e.message || 'Failed to load tutor earnings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(selectedMonth);
  }, [selectedMonth, load]);

  const goPrev = () => setSelectedMonth((ym) => shiftYm(ym, -1));
  const goNext = () => {
    setSelectedMonth((ym) => {
      const next = shiftYm(ym, +1);
      // Never navigate past the current month
      return next > today ? today : next;
    });
  };

  const rows = data?.tutors || [];
  const monthLabel = useMemo(() => formatMonthLabel(data?.month || selectedMonth), [data, selectedMonth]);
  const currency = data?.currency || 'USD';

  const exportCsv = () => {
    if (!rows.length) return;
    const columns = [
      { key: 'tutor_name', header: 'Tutor' },
      { key: 'tutor_id', header: 'Tutor ID' },
      { key: 'payment_count', header: 'Linked payments' },
      {
        key: 'projected_earnings',
        header: 'Projected earnings (70%)',
        format: (v) => (typeof v === 'number' ? v.toFixed(2) : v),
      },
      { key: 'currency', header: 'Currency', format: (v) => v || currency },
      { key: '__month', header: 'Month', format: () => data?.month || selectedMonth },
    ];
    downloadCsv(`tutor-earnings-${data?.month || selectedMonth}.csv`, rows, columns);
  };

  return (
    <Card data-testid="tutor-earnings-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Wallet className="h-5 w-5 text-indigo-600" />
              <span>Tutor Earnings —</span>

              {/* Month navigator */}
              <div
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1 py-0.5 ml-1"
                data-testid="tutor-earnings-month-nav"
              >
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={loading}
                  className="p-1 rounded hover:bg-white text-gray-600 disabled:opacity-40"
                  aria-label="Previous month"
                  title="Previous month"
                  data-testid="tutor-earnings-prev-month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span
                  className="text-sm font-semibold text-gray-900 min-w-[90px] text-center"
                  data-testid="tutor-earnings-selected-month"
                >
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={loading || isCurrent}
                  className="p-1 rounded hover:bg-white text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next month"
                  title={isCurrent ? 'Already on current month' : 'Next month'}
                  data-testid="tutor-earnings-next-month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Projected 70% share from successful Paystack payments
              {isCurrent ? ' this month' : ` in ${monthLabel}`}. View-only · no payouts.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-50 text-indigo-800 border border-indigo-200">
              Projection · not paid out
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={loading || rows.length === 0}
              className="gap-1.5"
              data-testid="tutor-earnings-export-csv"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(selectedMonth)}
              className="gap-1.5"
              disabled={loading}
              data-testid="tutor-earnings-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading projected earnings…
          </div>
        ) : error ? (
          <div className="text-center py-6 text-sm text-red-600">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div
            className="text-center py-10 text-gray-500"
            data-testid="tutor-earnings-empty"
          >
            <Wallet className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            No successful payments linked to any tutor in {monthLabel}.
          </div>
        ) : (
          <>
            {/* Grand total */}
            <div
              className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4"
              data-testid="tutor-earnings-total"
            >
              <div>
                <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">
                  Projected tutor payouts ({monthLabel})
                </p>
                <p className="text-2xl font-bold text-indigo-900 mt-0.5">
                  ${(data?.grand_total_projected ?? 0).toFixed(2)} {currency}
                </p>
              </div>
              <div className="text-right text-sm text-indigo-700">
                <p>{data?.tutor_count} tutors</p>
                <p className="text-xs opacity-80">
                  {data?.total_payments} successful payment{data?.total_payments === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table
                className="w-full text-sm"
                data-testid="tutor-earnings-table"
              >
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 tracking-wide">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Tutor</th>
                    <th className="px-3 py-2 font-semibold">Linked payments</th>
                    <th className="px-3 py-2 font-semibold">Projected share (70%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr
                      key={r.tutor_id || 'unassigned'}
                      className="hover:bg-gray-50"
                      data-testid={`tutor-earnings-row-${r.tutor_id || 'unassigned'}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.tutor_name}
                        {!r.tutor_id && (
                          <Badge className="ml-2 bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">
                            no linked tutor
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {r.payment_count}
                      </td>
                      <td className="px-3 py-2 text-emerald-700 font-semibold whitespace-nowrap">
                        ${r.projected_earnings.toFixed(2)} {r.currency || currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TutorEarningsCard;
